"use client";

import Image from "next/image";
import { useState } from "react";
import logoWhiteNoBg from "@/assets/imgs/logo-white-no-bg.png";
import clsx from "clsx";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import { useClientTranslations } from "@/lib/translate";
import { useEmployeeInfo } from "@/modules/hooks/employee/useEmployeeInfo";
import {
  Logout02Icon,
  Message01Icon,
  NewJobIcon,
  Notification01Icon,
  Settings01Icon,
  UserIcon,
} from "hugeicons-react";
import { EmployeeInfo } from "@/modules/types/employee/employee";

const PublicHeader = ({ employeeInfo }: { employeeInfo?: EmployeeInfo }) => {
  const { t } = useClientTranslations("header-public");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { logout } = useEmployeeInfo();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  return (
    <>
      <header
        className={clsx(
          "fixed w-full transition-all duration-300 z-50 flex justify-center h-[70px] bg-primary",
          "border-b border-gray-100 dark:border-gray-700 translate-y-0",
        )}
      >
        <div className="container flex justify-between items-center w-full px-2 md:px-8 text-white relative z-[51] bg-primary">
          <div className="flex gap-5 items-center">
            <Link href={routes.home}>
              <Image
                src={logoWhiteNoBg}
                width={455}
                height={144}
                alt="Together Work Logo"
                className="w-28"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex space-x-5">
              <Link
                className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full"
                href={routes.company.index}
              >
                {t("yourAreBusiness")}
              </Link>
              <Link
                className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full"
                href={routes.interviewer.index}
              >
                {t("yourAreContactInterviewer")}
              </Link>
              <Link
                className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full"
                href={routes.job.index}
              >
                {t("job")}
              </Link>
              <Link
                className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full"
                href={routes.company.list}
              >
                {t("company")}
              </Link>
              <Link
                className="relative after:absolute after:bottom-0 after:left-0 after:h-[1px] after:w-0 after:bg-current after:transition-all after:duration-300 hover:after:w-full"
                href="blog.dev.togetherwork.online"
              >
                {t("blog")}
              </Link>
            </div>
          </div>

          {/* Login Button and Hamburger Menu */}
          <div className="flex items-center gap-4">
            {employeeInfo ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center gap-2"
                >
                  <div className="w-8 h-8 rounded-full overflow-hidden">
                    {employeeInfo.avatar ? (
                      <Image
                        src={employeeInfo.avatar}
                        alt="User avatar"
                        width={32}
                        height={32}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-secondary flex items-center justify-center text-secondaryForeground">
                        {employeeInfo.lastName?.[0]?.toUpperCase() || "U"}
                      </div>
                    )}
                  </div>
                  <span className="hidden md:block">
                    {employeeInfo.firstName} {employeeInfo.lastName}
                  </span>
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <Link
                      href={routes.candidate.profile}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <UserIcon /> {t("profileMenu.profile")}
                    </Link>
                    <Link
                      href={routes.candidate.applicants}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <NewJobIcon />
                      {t("profileMenu.apply")}
                    </Link>
                    <Link
                      href={routes.candidate.message}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <Message01Icon />
                      {t("profileMenu.message")}
                    </Link>
                    <Link
                      href={routes.candidate.notification}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <Notification01Icon />
                      {t("profileMenu.notification")}
                    </Link>
                    <Link
                      href={routes.candidate.setting}
                      className="flex gap-3 items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <Settings01Icon />
                      {t("profileMenu.setting")}
                    </Link>
                    <button
                      onClick={logout}
                      className="flex gap-3 items-center w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-300"
                    >
                      <Logout02Icon />
                      {t("profileMenu.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                href={routes.auth.login}
                className="bg-secondary px-3 py-2 rounded-md text-secondaryForeground hover:bg-secondaryLighter transition-colors duration-300"
              >
                {t("login")}
              </Link>
            )}

            {/* Hamburger Menu Button */}
            <button
              className="lg:hidden flex flex-col justify-center items-center"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? "rotate-45 translate-y-1.5" : ""}`}
              ></span>
              <span
                className={`block w-6 h-0.5 bg-white my-1 transition-all duration-300 ${isMenuOpen ? "opacity-0" : ""}`}
              ></span>
              <span
                className={`block w-6 h-0.5 bg-white transition-all duration-300 ${isMenuOpen ? "-rotate-45 -translate-y-1.5" : ""}`}
              ></span>
            </button>
          </div>
        </div>
        <div className="h-[70px] w-full absolute top-0 left-0 bg-primary z-50"></div>

        {/* Mobile Navigation Menu */}
        <div
          className={clsx(
            "fixed top-[70px] left-0 w-full bg-primary transition-transform duration-300 lg:hidden z-40",
            isMenuOpen ? "translate-y-0 shadow-md" : "-translate-y-full",
          )}
        >
          <div className="flex flex-col p-4 space-y-4">
            <Link
              className="font-semibold text-white hover:text-gray-200 transition-colors duration-300"
              href={routes.company.index}
              onClick={toggleMenu}
            >
              {t("yourAreBusiness")}
            </Link>
            <Link
              className="font-semibold text-white hover:text-gray-200 transition-colors duration-300"
              href={routes.interviewer.index}
              onClick={toggleMenu}
            >
              {t("yourAreContactInterviewer")}
            </Link>
            <Link
              className="font-semibold text-white hover:text-gray-200 transition-colors duration-300"
              href={routes.job.index}
              onClick={toggleMenu}
            >
              {t("job")}
            </Link>
            <Link
              className="font-semibold text-white hover:text-gray-200 transition-colors duration-300"
              href={routes.company.list}
              onClick={toggleMenu}
            >
              {t("company")}
            </Link>
            <Link
              className="font-semibold text-white hover:text-gray-200 transition-colors duration-300"
              href="blog.dev.togetherwork.online"
              onClick={toggleMenu}
            >
              {t("blog")}
            </Link>
          </div>
        </div>
      </header>
      {/* Click outside to close user menu */}
      {isUserMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsUserMenuOpen(false)}
        />
      )}
    </>
  );
};

PublicHeader.displayName = "PublicHeader";
export default PublicHeader;
